import React, { Component } from 'react';

import { Box } from '@chakra-ui/react';
import { Dialog } from '@material-ui/core';
import * as Sentry from '@sentry/react';
import { matchPath } from 'react-router-dom';
import styled from 'styled-components';
import { StyledTypography } from 'styles/common';

import { supportEmail } from 'constants/ProjectConstants';
import { localStorageKeys } from 'constants/localStorageKeys';
import { sendErrorBoundaryCrashEvent } from 'constants/mixpanelHelper';
import { isNetchexLogin } from 'constants/netChexConstants';
import { routesForTracking } from 'constants/routesForTracking';

import Emoji from './Emoji';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { eventId: null };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // fail-safe in case any previous data shared in localStorage could be the cause of issue, or the structure of data stored is changed in new version
    Object.keys(localStorageKeys).forEach((key) => localStorage.removeItem(key));

    let pageName;
    try {
      const route = Object.values(routesForTracking).find((route) =>
        matchPath(window.location.pathname, { path: route.path, exact: true })
      );
      pageName = route ? route.pageName : null;
      const eventProperties = { errorMessage: error.message, pageName };
      const demoUserEmailLS = window.localStorage.getItem(localStorageKeys.demoUserEmail);
      if (demoUserEmailLS) {
        eventProperties.demoUserEmail = JSON.parse(demoUserEmailLS);
      }

      sendErrorBoundaryCrashEvent({ eventProperties });
    } catch (err) {
      console.log('error in error boundary');
    }

    Sentry.withScope((scope) => {
      scope.setExtras({ errorInfo, pageName });
      scope.setLevel('fatal');
      scope.setTag('severity', 1);
      scope.setTransactionName('ErrorBoundary');

      error.name = '[SEV1]: App failed';

      const eventId = Sentry.captureException(new Error('[SEV1]: App failed', { cause: error }));

      this.setState({ eventId });
    });
  }

  render() {
    if (this.state.hasError && this.props.showSimpleTextFallback) {
      return <Box>Some Error has occurred</Box>;
    }

    if (this.state.hasError) {
      if (isNetchexLogin) {
        return (
          <StyledDialog disableBackdropClick disableEscapeKeyDown aria-labelledby="simple-dialog-title" open>
            <DialogContainer>
              <div>
                <StyledTypography weight={800} variant="h5">
                  Something went wrong on Netchex's side
                  <Emoji label="cry-face" symbol="😢" />
                </StyledTypography>
              </div>
              <DialogContent>
                We couldn't load this page. You can try the following steps:
                <List>
                  <li>
                    <Link onClick={() => window.location.reload()}>Reload your page</Link> and try again.
                  </li>
                  <li>Check your internet connection or browser settings</li>
                </List>
                If the issue persists, please contact your admin.
              </DialogContent>
            </DialogContainer>
          </StyledDialog>
        );
      }

      // render fallback UI
      return (
        <StyledDialog disableBackdropClick disableEscapeKeyDown aria-labelledby="simple-dialog-title" open>
          <DialogContainer>
            <div>
              <StyledTypography weight={800} variant="h5">
                For some reason, Mesh couldn't process <Emoji label="cry-face" symbol="😢" />
              </StyledTypography>
            </div>
            <DialogContent>
              We are sorry about this. Please try the following:
              <List>
                <li>
                  <Link onClick={() => window.location.reload()}>Reload your page</Link> and try again.
                </li>
                <li>Verify your connection or security software.</li>
                <li>
                  Go back to <Link onClick={() => window.open('/', '_self')}>Homepage</Link>
                </li>
              </List>
              <Link onClick={() => window.open('https://www.mesh.ai/faqs', '_blank', 'noopener,noreferrer')}>
                Check our help center
              </Link>{' '}
              for more details or <Link href={`mailto:${supportEmail}`}>drop us a line</Link>.
            </DialogContent>
          </DialogContainer>
        </StyledDialog>
      );
    }

    // when there's not an error, render children untouched
    return this.props.children;
  }
}

const StyledDialog = styled(Dialog)`
  font-size: 1rem;
  .MuiBackdrop-root {
    background-color: #ddd;
  }
  h5 {
    font-size: 1.6em;
  }
`;

const DialogContainer = styled.div`
  padding: 1.2rem 1.7rem;
`;

const DialogContent = styled.div`
  padding: 0.75rem 0 0.75rem 0;
`;

const List = styled.ul`
  list-style-position: inside;
  padding-left: 3px;
`;

const Link = styled.a`
  cursor: pointer;
  color: #0992f0;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

export default ErrorBoundary;
